import type { AnchorHTMLAttributes, ButtonHTMLAttributes, HTMLAttributes, ReactNode } from "react";
import { motion } from "framer-motion";

type StatusKind = "success" | "live" | "idle" | "warning" | "info";

export function GTBrackets({ color = "border-primary/50" }: { color?: string }) {
  return (
    <>
      <div className={`pointer-events-none absolute left-0 top-0 z-10 h-2.5 w-2.5 border-l border-t ${color}`} />
      <div className={`pointer-events-none absolute right-0 top-0 z-10 h-2.5 w-2.5 border-r border-t ${color}`} />
      <div className={`pointer-events-none absolute bottom-0 left-0 z-10 h-2.5 w-2.5 border-b border-l ${color}`} />
      <div className={`pointer-events-none absolute bottom-0 right-0 z-10 h-2.5 w-2.5 border-b border-r ${color}`} />
    </>
  );
}

export function SectionHeader({
  eyebrow,
  title,
  description,
  align = "left",
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  align?: "left" | "center";
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      className={`mb-12 w-full ${align === "center" ? "text-center" : ""}`}
    >
      {eyebrow ? (
        <div className={`mb-4 flex items-center gap-4 ${align === "center" ? "justify-center" : ""}`}>
          <div className="h-px max-w-20 flex-1 bg-border" />
          <span className="text-[11px] font-semibold uppercase tracking-[0.3em] text-primary">{eyebrow}</span>
          <div className="h-px max-w-20 flex-1 bg-border" />
        </div>
      ) : null}
      <h2 className="font-display text-4xl font-bold uppercase tracking-tight text-text md:text-5xl">{title}</h2>
      {description ? (
        <p className={`mt-3 max-w-2xl text-base text-muted md:text-lg ${align === "center" ? "mx-auto" : ""}`}>
          {description}
        </p>
      ) : null}
    </motion.div>
  );
}

function actionClassName({
  variant = "primary",
  size = "md",
  className = "",
}: {
  variant?: "primary" | "ghost" | "accent";
  size?: "sm" | "md" | "lg";
  className?: string;
}) {
  const sizes = {
    sm: "px-4 py-2 text-xs",
    md: "px-6 py-3 text-sm",
    lg: "px-8 py-4 text-sm",
  };
  const variants = {
    primary: "border border-primary/80 bg-primary text-text hover:bg-primary-hover",
    ghost: "border border-border bg-transparent text-text hover:border-primary hover:text-primary",
    accent: "border border-accent/80 bg-accent text-text hover:bg-accent/90",
  };

  return `group relative inline-flex items-center justify-center gap-2 overflow-hidden rounded-md font-semibold uppercase tracking-[0.18em] transition duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-bg ${sizes[size]} ${variants[variant]} ${className}`;
}

export function Button({
  children,
  variant = "primary",
  size = "md",
  className = "",
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "ghost" | "accent";
  size?: "sm" | "md" | "lg";
}) {
  return (
    <button
      className={actionClassName({ variant, size, className })}
      {...props}
    >
      <span className="relative z-10 flex items-center gap-2">{children}</span>
    </button>
  );
}

export function LinkButton({
  children,
  variant = "primary",
  size = "md",
  className = "",
  ...props
}: AnchorHTMLAttributes<HTMLAnchorElement> & {
  variant?: "primary" | "ghost" | "accent";
  size?: "sm" | "md" | "lg";
}) {
  return (
    <a className={actionClassName({ variant, size, className })} {...props}>
      <span className="relative z-10 flex items-center gap-2">{children}</span>
    </a>
  );
}

export function Card({
  children,
  className = "",
  interactive = true,
  ...props
}: HTMLAttributes<HTMLDivElement> & { children?: ReactNode; interactive?: boolean }) {
  return (
    <div
      className={`group relative overflow-hidden rounded-xl border border-border bg-surface p-6 transition-colors duration-300 ${
        interactive ? "cursor-pointer hover:border-primary/60" : ""
      } ${className}`}
      {...props}
    >
      {children}
    </div>
  );
}

export function StatusPill({
  status = "success",
  label,
  pulse = false,
  className = "",
}: {
  status?: StatusKind;
  label: string;
  pulse?: boolean;
  className?: string;
}) {
  const colors: Record<StatusKind, string> = {
    success: "text-success",
    live: "text-accent",
    idle: "text-muted",
    warning: "text-warning",
    info: "text-primary",
  };
  const dotColors: Record<StatusKind, string> = {
    success: "bg-success",
    live: "bg-accent",
    idle: "bg-muted",
    warning: "bg-warning",
    info: "bg-primary",
  };

  return (
    <span
      className={`inline-flex items-center gap-2 rounded-md border border-border bg-surface-2 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] ${colors[status]} ${className}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${dotColors[status]} ${pulse ? "pulse-dot" : ""}`} />
      {label}
    </span>
  );
}

export function IBoxLogo({ className = "" }: { className?: string }) {
  return <img src="/iBox_Logo_Clean.svg" alt="iBox" className={`block w-auto ${className}`} />;
}
